import React, { useEffect, useState, useCallback } from "react";
import { CalendarDays, Save, Plus, Trash2, Check, X, CalendarOff, MessageSquare } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Select, Modal } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { BranchFilter } from "../../components/finance";

const TABS = [
  { id: "schedule", label: "Jadwal" },
  { id: "leave", label: "Cuti / Izin" },
  { id: "holidays", label: "Hari Libur" },
  { id: "settings", label: "Pengaturan" },
];

const TYPE_TONE = { work: "info", off: "mute", holiday: "warn" };
const TYPE_LABEL = { work: "Kerja", off: "Libur", holiday: "Hari Libur" };

export default function HRSchedule() {
  const toast = useToast();
  const { user } = useAuth();
  const isBranchMgr = user?.role === "BRANCH_MANAGER";
  const canManage = ["SUPER_ADMIN", "OWNER", "HR_MANAGER"].includes(user?.role);
  const [tab, setTab] = useState("schedule");

  return (
    <div data-testid="hr-schedule-page">
      <PageHeader title="Jadwal & Cuti" subtitle="Atur jadwal kerja, hari libur & kelola pengajuan cuti" icon={CalendarDays} />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="hr-schedule-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`hr-schedule-tab-${t.id}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === t.id ? "bg-primary text-white" : "bg-stone-100 text-ink-soft hover:bg-stone-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "schedule" && <ScheduleTab canManage={canManage} isBranchMgr={isBranchMgr} toast={toast} />}
      {tab === "leave" && <LeaveTab canManage={canManage} isBranchMgr={isBranchMgr} toast={toast} />}
      {tab === "holidays" && <HolidaysTab canManage={canManage} toast={toast} />}
      {tab === "settings" && <SettingsTab canManage={canManage} toast={toast} />}
    </div>
  );
}

function DayOffCard({ branchId, isBranchMgr, toast, onDone }) {
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [scope, setScope] = useState("individual");
  const [date, setDate] = useState("");
  const [label, setLabel] = useState("Day Off Rutin");
  const [empIds, setEmpIds] = useState([]);
  const [branchIds, setBranchIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/hr/branches").then(({ data }) => setBranches(data.rows)).catch(() => {});
    api.get("/hr/employees", { params: { branch_id: branchId || undefined } })
      .then(({ data }) => setEmployees(data.rows)).catch(() => {});
  }, [branchId]);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const assign = async () => {
    if (!date) return toast.error("Pilih tanggal");
    const body = { date, label, scope };
    if (scope === "individual") { if (!empIds.length) return toast.error("Pilih karyawan"); body.employee_ids = empIds; }
    else { if (!branchIds.length) return toast.error("Pilih cabang"); body.branch_ids = branchIds; }
    setSaving(true);
    try {
      const { data } = await api.post("/hr/day-off", body);
      toast.success(`Day Off ditetapkan untuk ${data.assigned} karyawan`);
      setEmpIds([]); setBranchIds([]); setDate(""); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSaving(false); }
  };

  return (
    <Card data-testid="hr-dayoff-card">
      <h3 className="mb-1 flex items-center gap-2 font-heading font-bold text-ink"><CalendarOff size={18} /> Tetapkan Day Off</h3>
      <p className="mb-3 text-xs text-ink-mute">Individu, satu cabang, atau beberapa cabang sekaligus (Day Off Bersama).</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="cf-label">Cakupan</label>
          <Select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="hr-dayoff-scope">
            <option value="individual">Individu</option>
            <option value="branch">Satu Cabang</option>
            {!isBranchMgr && <option value="multi_branch">Beberapa Cabang (Bersama)</option>}
          </Select>
        </div>
        <Input label="Tanggal" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="hr-dayoff-date" />
        <Input label="Label" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="hr-dayoff-label" />
      </div>
      {scope === "individual" ? (
        <div className="mt-3"><label className="cf-label">Karyawan</label>
          <div className="flex flex-wrap gap-2" data-testid="hr-dayoff-employees">
            {employees.map((e) => (
              <button key={e.id} type="button" onClick={() => toggle(empIds, setEmpIds, e.id)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${empIds.includes(e.id) ? "bg-primary text-white" : "bg-stone-100 text-ink-soft"}`}>{e.full_name}</button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-3"><label className="cf-label">Cabang</label>
          <div className="flex flex-wrap gap-2" data-testid="hr-dayoff-branches">
            {branches.map((b) => (
              <button key={b.id} type="button" onClick={() => toggle(branchIds, setBranchIds, b.id)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${branchIds.includes(b.id) ? "bg-primary text-white" : "bg-stone-100 text-ink-soft"}`}>{b.name}</button>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4"><Button onClick={assign} loading={saving} data-testid="hr-dayoff-assign"><CalendarOff size={15} /> Tetapkan Day Off</Button></div>
    </Card>
  );
}

function ScheduleTab({ canManage, isBranchMgr, toast }) {
  const [branchId, setBranchId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState("");
  const [weekly, setWeekly] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/hr/employees", { params: { branch_id: branchId || undefined } })
      .then(({ data }) => { setEmployees(data.rows); setEmpId((id) => id || data.rows[0]?.id); })
      .catch(() => toast.error("Gagal memuat karyawan"));
  }, [branchId, toast]);

  const loadSchedule = useCallback(async () => {
    if (!empId) return;
    setLoading(true);
    try { const { data } = await api.get("/hr/schedule", { params: { employee_id: empId } }); setWeekly(data.weekly); }
    catch { toast.error("Gagal memuat jadwal"); } finally { setLoading(false); }
  }, [empId, toast]);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const setDay = async (date, type) => {
    try {
      await api.post("/hr/schedule", { employee_id: empId, date, type, label: type === "off" ? "Libur" : null });
      toast.success("Jadwal diperbarui"); loadSchedule();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {!isBranchMgr && <BranchFilter value={branchId} onChange={setBranchId} testid="hr-schedule-branch-filter" />}
        <Select value={empId} onChange={(e) => setEmpId(e.target.value)} data-testid="hr-schedule-employee" className="!w-56">
          {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} · {e.position}</option>)}
        </Select>
      </div>
      <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
        <CalendarOff size={14} className="mt-0.5 shrink-0" /> Karyawan bekerja setiap hari (kebijakan restoran). Tetapkan <b>Libur</b> secara manual — tidak ada akhir pekan otomatis.
      </div>
      {canManage && <DayOffCard branchId={branchId} isBranchMgr={isBranchMgr} toast={toast} onDone={loadSchedule} />}
      {loading ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : (
        <Card data-testid="hr-schedule-week">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {weekly.map((d) => (
              <div key={d.date} className="rounded-xl border border-line p-3" data-testid={`hr-schedule-day-${d.date}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-ink">{d.date}</span>
                  <Badge tone={TYPE_TONE[d.type]}>{d.label || TYPE_LABEL[d.type]}</Badge>
                </div>
                {d.type !== "holiday" && <p className="mt-1 text-xs text-ink-mute">{d.shift_start} – {d.shift_end}</p>}
                {canManage && d.type !== "holiday" && (
                  <div className="mt-2 flex gap-2">
                    {d.type === "work"
                      ? <Button size="sm" variant="outline" onClick={() => setDay(d.date, "off")} data-testid={`hr-schedule-setoff-${d.date}`}>Set Libur</Button>
                      : <Button size="sm" variant="outline" onClick={() => setDay(d.date, "work")} data-testid={`hr-schedule-setwork-${d.date}`}>Set Kerja</Button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function LeaveTab({ canManage, isBranchMgr, toast }) {
  const { user } = useAuth();
  // Approval/Rejection is EXCLUSIVELY Owner + Super Admin. Branch Managers / HR are view-only.
  const canDecide = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const canComment = canManage || isBranchMgr;
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentFor, setCommentFor] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/leave", { params: { status: status || undefined } }); setRows(data.rows); }
    catch { toast.error("Gagal memuat cuti"); } finally { setLoading(false); }
  }, [status, toast]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await api.patch(`/hr/leave/${id}`, { status: "approved" }); toast.success("Cuti disetujui"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const submitReject = async () => {
    try { await api.patch(`/hr/leave/${rejectFor.id}`, { status: "rejected", notes: rejectNote || null }); toast.success("Cuti ditolak"); setRejectFor(null); setRejectNote(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const submitComment = async () => {
    if (!commentText.trim()) return;
    try { await api.post(`/hr/leave/${commentFor.id}/comment`, { comment: commentText.trim() }); toast.success("Komentar ditambahkan"); setCommentFor(null); setCommentText(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const TONE = { pending: "warn", approved: "success", rejected: "danger", cancelled: "mute" };
  const LBL = { pending: "Menunggu", approved: "Disetujui", rejected: "Ditolak", cancelled: "Dibatalkan" };

  return (
    <div className="space-y-4">
      {!canDecide && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="hr-leave-viewonly-note">
          <MessageSquare size={14} className="mt-0.5 shrink-0" /> Persetujuan/penolakan cuti hanya oleh <b>Owner</b>. Anda dapat melihat, memfilter, dan menambahkan komentar internal.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {["pending", "approved", "rejected", ""].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`hr-leave-filter-${s || "all"}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${status === s ? "bg-ink text-white" : "bg-stone-100 text-ink-soft"}`}>
            {s ? LBL[s] : "Semua"}
          </button>
        ))}
      </div>
      {loading ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : (
        <Card className="overflow-x-auto" data-testid="hr-leave-table">
          {rows.length === 0 ? <p className="py-6 text-center text-sm text-ink-mute">Tidak ada pengajuan</p> : (
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <tr><th className="py-2 pr-3">Karyawan</th><th className="py-2 pr-3">Cabang</th><th className="py-2 pr-3">Periode</th><th className="py-2 pr-3 text-right">Hari</th><th className="py-2 pr-3">Alasan</th><th className="py-2 pr-3">Status</th><th /></tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`hr-leave-row-${r.id}`}>
                    <td className="py-2.5 pr-3 font-medium text-ink">{r.employee_name}</td>
                    <td className="py-2.5 pr-3 text-ink-soft">{r.branch_name || "—"}</td>
                    <td className="py-2.5 pr-3 text-ink-soft">{r.start_date} → {r.end_date}</td>
                    <td className="py-2.5 pr-3 text-right text-ink-soft">{r.days}</td>
                    <td className="py-2.5 pr-3 text-ink-soft">
                      {r.reason || "—"}
                      {r.decided_by && <span className="block text-[11px] text-ink-mute">{r.status === "approved" ? "Disetujui" : r.status === "rejected" ? "Ditolak" : ""} oleh {r.decided_by}</span>}
                      {(r.internal_comments || []).length > 0 && <span className="mt-0.5 block text-[11px] text-indigo-500">{r.internal_comments.length} komentar internal</span>}
                    </td>
                    <td className="py-2.5 pr-3"><Badge tone={TONE[r.status]}>{LBL[r.status]}</Badge></td>
                    <td className="py-2.5 pr-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        {canDecide && r.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => approve(r.id)} data-testid={`hr-leave-approve-${r.id}`}><Check size={14} /> Setuju</Button>
                            <Button size="sm" variant="outline" onClick={() => { setRejectFor(r); setRejectNote(""); }} data-testid={`hr-leave-reject-${r.id}`}><X size={14} /> Tolak</Button>
                          </>
                        )}
                        {canComment && (
                          <Button size="sm" variant="outline" onClick={() => { setCommentFor(r); setCommentText(""); }} data-testid={`hr-leave-comment-${r.id}`}><MessageSquare size={14} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      <Modal open={!!commentFor} onClose={() => setCommentFor(null)} title="Komentar Internal" size="sm"
        footer={<><Button variant="outline" onClick={() => setCommentFor(null)}>Batal</Button><Button onClick={submitComment} data-testid="hr-leave-comment-save">Simpan</Button></>}>
        {commentFor && (
          <div className="space-y-2">
            <p className="text-sm text-ink-soft">{commentFor.employee_name} · {commentFor.start_date} → {commentFor.end_date}</p>
            {(commentFor.internal_comments || []).length > 0 && (
              <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-lg bg-stone-50 p-2">
                {commentFor.internal_comments.map((c, i) => (
                  <div key={i} className="text-xs"><span className="font-semibold text-ink">{c.by}:</span> <span className="text-ink-soft">{c.comment}</span></div>
                ))}
              </div>
            )}
            <textarea className="cf-input" rows={3} value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Catatan internal (tidak terlihat karyawan)" data-testid="hr-leave-comment-input" />
          </div>
        )}
      </Modal>

      <Modal open={!!rejectFor} onClose={() => setRejectFor(null)} title="Tolak Pengajuan" size="sm"
        footer={<><Button variant="outline" onClick={() => setRejectFor(null)}>Batal</Button><Button onClick={submitReject} data-testid="hr-leave-reject-confirm"><X size={14} /> Tolak</Button></>}>
        {rejectFor && (
          <div className="space-y-2">
            <p className="text-sm text-ink-soft">{rejectFor.employee_name} · {rejectFor.start_date} → {rejectFor.end_date}</p>
            <textarea className="cf-input" rows={3} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Alasan penolakan (opsional, dikirim ke karyawan)" data-testid="hr-leave-reject-note" />
          </div>
        )}
      </Modal>
    </div>
  );
}

function HolidaysTab({ canManage, toast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: "", name: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/holidays"); setRows(data.rows); }
    catch { toast.error("Gagal memuat hari libur"); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.date || !form.name) return toast.error("Tanggal & nama wajib diisi");
    try { await api.post("/hr/holidays", form); toast.success("Hari libur ditambahkan"); setForm({ date: "", name: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (id) => {
    try { await api.delete(`/hr/holidays/${id}`); toast.success("Dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <Card data-testid="hr-holiday-form">
          <h3 className="mb-3 font-heading font-bold text-ink">Tambah Hari Libur</h3>
          <div className="flex flex-wrap items-end gap-3">
            <div><label className="cf-label">Tanggal</label><input type="date" className="cf-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="hr-holiday-date" /></div>
            <div className="flex-1"><label className="cf-label">Nama</label><input className="cf-input" placeholder="cth. Tahun Baru" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hr-holiday-name" /></div>
            <Button onClick={add} data-testid="hr-holiday-add"><Plus size={15} /> Tambah</Button>
          </div>
        </Card>
      )}
      {loading ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : (
        <Card data-testid="hr-holiday-list">
          {rows.length === 0 ? <p className="py-6 text-center text-sm text-ink-mute">Belum ada hari libur</p> : (
            <ul className="divide-y divide-line">
              {rows.map((h) => (
                <li key={h.id} className="flex items-center justify-between py-2.5" data-testid={`hr-holiday-${h.id}`}>
                  <span className="text-sm"><b className="text-ink">{h.date}</b> · <span className="text-ink-soft">{h.name}</span></span>
                  {canManage && <button onClick={() => del(h.id)} className="rounded-lg p-1.5 text-danger-text hover:bg-red-50" data-testid={`hr-holiday-del-${h.id}`}><Trash2 size={15} /></button>}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}

function BranchHoursCard({ toast }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState("");

  useEffect(() => { api.get("/hr/branches").then(({ data }) => setRows(data.rows)).catch(() => {}); }, []);
  const setField = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));

  const save = async (r) => {
    if (!r.work_start || !r.work_end) return toast.error("Isi jam masuk & pulang");
    setSaving(r.id);
    try { await api.patch(`/hr/branches/${r.id}/hours`, { work_start: r.work_start, work_end: r.work_end }); toast.success(`Jam kerja ${r.name} tersimpan`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSaving(""); }
  };

  return (
    <Card data-testid="hr-branch-hours">
      <h3 className="mb-1 font-heading font-bold text-ink">Jam Kerja per Cabang</h3>
      <p className="mb-3 text-xs text-ink-mute">Satu jadwal kerja standar per cabang (tanpa rotasi shift).</p>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-end gap-3 rounded-xl border border-line p-3" data-testid={`hr-branch-hours-${r.id}`}>
            <span className="min-w-[120px] font-semibold text-ink">{r.name}</span>
            <div><label className="cf-label">Masuk</label><input type="time" className="cf-input !w-32" value={r.work_start || ""} onChange={(e) => setField(r.id, "work_start", e.target.value)} data-testid={`hr-branch-start-${r.id}`} /></div>
            <div><label className="cf-label">Pulang</label><input type="time" className="cf-input !w-32" value={r.work_end || ""} onChange={(e) => setField(r.id, "work_end", e.target.value)} data-testid={`hr-branch-end-${r.id}`} /></div>
            <Button size="sm" onClick={() => save(r)} loading={saving === r.id} data-testid={`hr-branch-save-${r.id}`}><Save size={14} /> Simpan</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SettingsTab({ canManage, toast }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/hr/hr-settings").then(({ data }) => setS(data)).catch(() => toast.error("Gagal memuat pengaturan")); }, [toast]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/hr/hr-settings", {
        monthly_leave_quota: Number(s.monthly_leave_quota), shift_start: s.shift_start,
        shift_end: s.shift_end, late_grace_min: Number(s.late_grace_min), standard_hours: Number(s.standard_hours),
        tz_offset_minutes: Number(s.tz_offset_minutes), gps_accuracy_threshold_m: Number(s.gps_accuracy_threshold_m),
        geofence_required: s.geofence_required !== false,
      });
      toast.success("Pengaturan tersimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSaving(false); }
  };

  if (!s) return <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div>;
  return (
    <div className="space-y-4">
      {canManage && <BranchHoursCard toast={toast} />}
      <Card data-testid="hr-settings-form">
      <h3 className="mb-1 font-heading font-bold text-ink">Pengaturan HR</h3>
      <p className="mb-3 text-xs text-ink-mute">Jatah cuti dihitung: hari kerja per bulan dikurangi cuti yang disetujui.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Jatah Cuti / bulan (hari)" type="number" value={s.monthly_leave_quota} disabled={!canManage} onChange={(e) => setS({ ...s, monthly_leave_quota: e.target.value })} data-testid="hr-settings-quota" />
        <Input label="Toleransi Telat (menit)" type="number" value={s.late_grace_min} disabled={!canManage} onChange={(e) => setS({ ...s, late_grace_min: e.target.value })} data-testid="hr-settings-grace" />
        <Input label="Jam Masuk" value={s.shift_start} disabled={!canManage} onChange={(e) => setS({ ...s, shift_start: e.target.value })} data-testid="hr-settings-start" />
        <Input label="Jam Pulang" value={s.shift_end} disabled={!canManage} onChange={(e) => setS({ ...s, shift_end: e.target.value })} data-testid="hr-settings-end" />
        <Input label="Jam Kerja Standar" type="number" value={s.standard_hours} disabled={!canManage} onChange={(e) => setS({ ...s, standard_hours: e.target.value })} data-testid="hr-settings-hours" />
        <Input label="Zona Waktu (menit dari UTC, WIB=420)" type="number" value={s.tz_offset_minutes} disabled={!canManage} onChange={(e) => setS({ ...s, tz_offset_minutes: e.target.value })} data-testid="hr-settings-tz" />
        <Input label="Ambang Akurasi GPS (meter)" type="number" value={s.gps_accuracy_threshold_m} disabled={!canManage} onChange={(e) => setS({ ...s, gps_accuracy_threshold_m: e.target.value })} data-testid="hr-settings-gps-accuracy" />
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-ink-soft">
          <input type="checkbox" checked={s.geofence_required !== false} disabled={!canManage} onChange={(e) => setS({ ...s, geofence_required: e.target.checked })} data-testid="hr-settings-geofence-required" />
          Wajibkan validasi geofence GPS saat absen
        </label>
      </div>
      {canManage && <div className="mt-4"><Button onClick={save} loading={saving} data-testid="hr-settings-save"><Save size={15} /> Simpan Pengaturan</Button></div>}
      </Card>
    </div>
  );
}
